import { createTheme } from '@mui/material/styles';
import type { CSSProperties } from 'react';
import { QualityLevel } from '@/api-gen/api.schemas';

// Quality level color mapping for theme
const QualityLevelColor = {
  [QualityLevel.Unset]: '#808080',
  [QualityLevel.Good]: '#4CAF50',
  [QualityLevel.Acceptable]: '#FFEB3B',
  [QualityLevel.Bad]: '#F44336'
} as const;

type QualityLevelColorType = typeof QualityLevelColor;
import { inputBaseClasses } from '@mui/material/InputBase';
import { Opacity } from '@mui/icons-material';

declare module '@mui/styles' {
  interface DefaultTheme {
    spacing: (value1: number, value2?: number, value3?: number, value4?: number) => string | number;
    transitions: {
      create: (
        props: string | string[],
        options?: {
          duration?: number | { shortest: number; shorter: number; short: number; standard: number; complex: number; enteringScreen: number; leavingScreen: number; };
          easing?: string | { easeInOut: string; easeOut: string; easeIn: string; sharp: string; };
          delay?: number;
        }
      ) => string;
      duration: {
        shortest: number;
        shorter: number;
        short: number;
        standard: number;
        complex: number;
        enteringScreen: number;
        leavingScreen: number;
      };
      easing: {
        easeInOut: string;
        easeOut: string;
        easeIn: string;
        sharp: string;
      };
    };
    shape: {
      borderRadius: number;
    };
    palette: {
      action: {
        hover: string;
      };
      divider: string;
    };
  }
}

declare module '@mui/material/styles/createPalette.js' {
  interface Palette {
    qualityLevel: QualityLevelColorType;
  }

  interface PaletteOptions {
    qualityLevel: QualityLevelColorType;
  }

  interface TypeBackground {
    dark?: {
      400: string,
      500: string,
      600: string,
    }
  }

  interface PaletteColor {
    950?: string;
  }

  interface SimplePaletteColorOptions {
    950?: string;
  }

}

declare module '@mui/material/styles/createMixins.js' {
  interface Mixins {
    denseToolbar: CSSProperties;
  }
}

const theme = createTheme({
  mixins: {
    denseToolbar: {
      minHeight: 72
    },
  },
  palette: {
    secondary: {
      main: '#F57A3D',
      50: '#FEF5EE',
      100: '#FEE8D6',
      200: '#FBCEAD',
      300: '#F9AB78',
      400: '#F57A3D',
      500: '#F25B1D',
      600: '#E34113',
      700: '#BD2F11',
      800: '#962716',
      900: '#792315',
      950: '#410E09'
    },
    primary: {
      light: '#374B9C',
      main: '#0F1324',
      50: '#EFF7FE',
      100: '#E2EFFD',
      200: '#CAE0FB',
      300: '#AACBF7',
      400: '#88ADF1',
      500: '#6C8DE8',
      600: '#506BDB',
      700: '#374B9C',
      800: '#374B9C',
      900: '#33427C',
      950: '#0F1324',
    },
    grey: {
      50: '#F6F6F6',
      100: '#E7E7E7',
      200: '#D1D1D1',
      300: '#B0B0B0',
      400: '#888888',
      500: '#6D6D6D',
      600: '#5D5D5D',
      700: '#4F4F4F',
      800: '#454545',
      900: '#3D3D3D',
    },
    background: {
      dark: {
        400: '#4629F2',
        500: '#1D1748',
        600: '#0D082C'
      }
    },
    success: {
      main: '#007D3E'
    },
    error: {
      main: '#CC071E'
    },
    info: {
      main: '#0065CB'
    },
    qualityLevel: QualityLevelColor
  },
  components: {
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none'
        }
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          '&.MuiButton-containedPrimary:hover': {
            backgroundColor: 'rgba(15, 19, 36, 0.85)', // Primary 950 with 0.85 opacity. Remove or change this if using something else than 950 as primary color
          }
        }
      }
    },
    MuiFab: {
      styleOverrides: {
        root: {
          '&.MuiFab-primary:hover': {
            backgroundColor: 'rgba(15, 19, 36, 0.85)', // Primary 950 with 0.85 opacity. Remove or change this if using something else than 950 as primary color
          }
        }
      }
    },
    MuiCssBaseline: {
      styleOverrides: {
        '.text-truncate': {
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        },
        '.full-width': {
          width: '100% !important'
        }
      }
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          // https://github.com/mui-org/material-ui/issues/25064#issuecomment-783948395
          backgroundColor: 'inherit'
        }
      }
    },
    MuiListSubheader: {
      styleOverrides: {
        root: {
          // https://github.com/mui-org/material-ui/issues/18200#issuecomment-578349237
          pointerEvents: 'none'
        }
      }
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          [`&.${inputBaseClasses.disabled}`]: {
            color: '#777'
          }
        }
      }
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          }
        }
      }
    }
  }
});

export default theme;
